import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  Card,
  Avatar,
  useTheme,
  InputAdornment,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import * as XLSX from "xlsx";
import { getDailySessionsStatistics } from "../../../../services/Reports/ReportsApi";
import { ReportStatisticsUtils } from "./Printing/printDailyReportUtils";
import { getActiveDailySessionData } from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";
import { useFeatures } from "../../../../context/PermissionsAndFeatures/FeaturesContext";

const StatCard = ({ icon, label, value, color, isDark }) => (
  <Card
    sx={{
      display: "flex",
      alignItems: "center",
      p: 2,
      borderRadius: 3,
      boxShadow: 3,
      bgcolor: isDark ? "#232334" : "#fff",
      minHeight: 90,
      color: isDark ? "#fff" : "#232334",
    }}
  >
    <Avatar
      sx={{
        bgcolor: color,
        width: 56,
        height: 56,
        mr: 2,
        boxShadow: 2,
        color: "#fff",
      }}
    >
      {icon}
    </Avatar>
    <Box>
      <Typography variant="subtitle2" color={isDark ? "#aaa" : "text.secondary"} fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={900} color={color}>
        {value}
      </Typography>
    </Box>
  </Card>
);

// YYYY-MM-DD -> YYYY/M/D (زي Postman عندك)
const toApiDate = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return "";
  const [y, m, d] = yyyy_mm_dd.split("-");
  return `${y}/${Number(m)}/${Number(d)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const ReportStatistics = () => {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(todayISO());

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { hasFeature, featuresLoading } = useFeatures();
  const salesStatisticsEnabled = !featuresLoading && hasFeature("sales_statistics");

  const fetchStatistics = async () => {
    setError(null);

    if (!fromDate || !toDate) {
      setError("⚠️ من فضلك اختر تاريخ (من) و(إلى).");
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      setError("⚠️ تاريخ (من) لازم يكون قبل أو يساوي تاريخ (إلى).");
      return;
    }

    setLoading(true);
    try {
      const stats = await getDailySessionsStatistics(toApiDate(fromDate), toApiDate(toDate));
      setStatistics(Array.isArray(stats) ? stats : []);
    } catch (err) {
      setError("❌ فشل في جلب إحصائيات المبيعات للفترة المختارة.");
      setStatistics([]);
    } finally {
      setLoading(false);
    }
  };

  // تحميل أولي لبيانات اليوم (بعد ما نعرف الميزة متاحة)
  useEffect(() => {
    if (!featuresLoading && salesStatisticsEnabled) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresLoading, salesStatisticsEnabled]);

  // جلب تاريخ اليومية النشطة واستخدامه كقيمة افتراضية لفلتر التاريخ (من - إلى)
  useEffect(() => {
    let mounted = true;
    const loadSessionDate = async () => {
      try {
        const branchId = localStorage.getItem("branch_id");
        const resp = await getActiveDailySessionData(branchId);
        const sessionDate = resp?.session_date || resp?.data?.session_date || null;
        if (mounted && sessionDate) {
          setFromDate(sessionDate);
          setToDate(sessionDate);
        }
      } catch (err) {
        // ignore, keep defaults
        console.warn("⚠️ فشل جلب تاريخ اليومية النشطة:", err);
      }
    };

    loadSessionDate();
    return () => {
      mounted = false;
    };
  }, []);

  // ✅ حسابات (بدون useMemo)
  const totalSold = statistics.reduce((acc, item) => acc + (Number(item.total_sold) || 0), 0);
  const totalAmount = statistics.reduce((acc, item) => acc + (Number(item.total_amount) || 0), 0);

  const topProduct =
    statistics.length > 0
      ? statistics.reduce((max, item) => ((item.total_sold || 0) > (max.total_sold || 0) ? item : max), statistics[0])
      : null;

  const topRevenueProduct =
    statistics.length > 0
      ? statistics.reduce(
          (max, item) => ((item.total_amount || 0) > (max.total_amount || 0) ? item : max),
          statistics[0]
        )
      : null;

  const exportToExcel = () => {
    if (filteredStatistics.length === 0) return;

    const exportData = filteredStatistics.map((item, idx) => ({
      "#": idx + 1,
      "اسم المنتج": item.product_name,
      "الكمية المباعة": item.total_sold,
      "الإيراد": Number(item.total_amount || 0),
    }));

    // إضافة صف الإجماليات
    exportData.push({
      "#": "",
      "اسم المنتج": "الإجمالي",
      "الكمية المباعة": totalSold,
      "الإيراد": totalAmount,
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "إحصائيات المبيعات");

    // ضبط عرض الأعمدة
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 30 },
      { wch: 18 },
      { wch: 18 },
    ];

    XLSX.writeFile(workbook, `إحصائيات_المبيعات_${fromDate}_${toDate}.xlsx`);
  };

  const handlePrintStatistics = async () => {
    if (!statistics || statistics.length === 0) return;
    const options = {
      fromDate,
      toDate,
      totalSold,
      totalAmount,
      title: "إحصائيات الاصناف",
    };
    try {
      await ReportStatisticsUtils(statistics, options);
    } catch (err) {
      console.error("❌ فشل في طباعة الإحصائيات:", err);
      alert("فشل في تجهيز طباعة الإحصائيات.");
    }
  };

  const filteredStatistics = statistics.filter((item) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (item.product_name || "").toLowerCase().includes(q) ||
      String(item.total_sold || "").includes(q) ||
      String(item.total_amount || "").includes(q)
    );
  });

  // ✅ Early returns بعد ما كل Hooks اتنداهت
  if (featuresLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!salesStatisticsEnabled) {
    return (
      <Paper
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 4,
          boxShadow: 6,
          bgcolor: isDark ? "#232334" : "#fff",
          color: isDark ? "#fff" : "#232334",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" color="error" align="center">
          🚫 ميزة إحصائيات المبيعات (sales_statistics) غير مفعّلة في حسابك.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: 4,
        boxShadow: 6,
        bgcolor: isDark ? "#232334" : "#fff",
        color: isDark ? "#fff" : "#232334",
        minHeight: 400,
      }}
    >
      <Typography
        variant="h4"
        fontWeight={900}
        gutterBottom
        sx={{
          textAlign: "center",
          color: isDark ? "#f48fb1" : "#1976d2",
          mb: 3,
          letterSpacing: 1,
        }}
      >
        📊 إحصائيات المنتجات (حسب الفترة)
      </Typography>

      {/* فلترة التاريخ + زر جلب */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="من"
            type="date"
            fullWidth
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="إلى"
            type="date"
            fullWidth
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <Button
            variant="contained"
            fullWidth
            onClick={fetchStatistics}
            disabled={loading}
            sx={{ height: 40, fontWeight: 800 }}
          >
            {loading ? "جاري الجلب..." : "عرض الاحصائيات"}
          </Button>
        </Grid>

        <Grid item xs={12} sm={4} md={1.5}>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              fullWidth
              onClick={exportToExcel}
              disabled={loading || filteredStatistics.length === 0}
              startIcon={<FileDownloadIcon />}
              sx={{ height: 40, fontWeight: 800 }}
              color="success"
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handlePrintStatistics}
              disabled={loading || statistics.length === 0}
              startIcon={<PrintIcon />}
              sx={{ height: 40, fontWeight: 800 }}
              color="primary"
            >
              طباعة
            </Button>
          </Stack>
        </Grid>

        <Grid item xs={12} md={1.5}>
          <Typography variant="body2" color={isDark ? "#aaa" : "text.secondary"}>
            سيتم عرض: {toApiDate(fromDate)} → {toApiDate(toDate)}
          </Typography>
        </Grid>
      </Grid>
      

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 16 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<BarChartIcon fontSize="large" />}
                label="إجمالي الكمية المباعة"
                value={totalSold}
                color="#1976d2"
                isDark={isDark}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<TrendingUpIcon fontSize="large" />}
                label="إجمالي الإيراد"
                value={totalAmount.toLocaleString() + " ج.م"}
                color="#43a047"
                isDark={isDark}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<ShoppingCartIcon fontSize="large" />}
                label="عدد المنتجات"
                value={statistics.length}
                color="#f48fb1"
                isDark={isDark}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, bgcolor: isDark ? "#29293a" : "#f5f5f5" }}>
                <Typography variant="subtitle1" fontWeight={700} color="#1976d2">
                  🏆 الأعلى مبيعاً
                </Typography>
                {topProduct ? (
                  <Box>
                    <Typography variant="h6" fontWeight={900}>{topProduct.product_name}</Typography>
                    <Typography color={isDark ? "#aaa" : "text.secondary"}>الكمية: {topProduct.total_sold}</Typography>
                  </Box>
                ) : (
                  <Typography color={isDark ? "#aaa" : "text.secondary"}>لا يوجد بيانات</Typography>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, bgcolor: isDark ? "#29293a" : "#f5f5f5" }}>
                <Typography variant="subtitle1" fontWeight={700} color="#43a047">
                  💰 الأعلى إيراداً
                </Typography>
                {topRevenueProduct ? (
                  <Box>
                    <Typography variant="h6" fontWeight={900}>{topRevenueProduct.product_name}</Typography>
                    <Typography color={isDark ? "#aaa" : "text.secondary"}>
                      الإيراد: {Number(topRevenueProduct.total_amount || 0).toLocaleString()} ج.م
                    </Typography>
                  </Box>
                ) : (
                  <Typography color={isDark ? "#aaa" : "text.secondary"}>لا يوجد بيانات</Typography>
                )}
              </Card>
            </Grid>
          </Grid>

          {/* البحث */}
          <Box sx={{ mb: 2, maxWidth: 350, ml: "auto" }}>
            <TextField
              variant="outlined"
              placeholder="بحث عن منتج أو كمية أو إيراد..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BarChartIcon color="primary" />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: isDark ? "#29293a" : "#f5f5f5",
                  borderRadius: 2,
                },
              }}
            />
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#1976d2" }}>
              تفاصيل المنتجات
            </Typography>

            <Table size="medium" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? "#29293a" : "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>اسم المنتج</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>الكمية المباعة</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>الإيراد</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredStatistics.map((item, idx) => (
                  <TableRow key={item.product_id ?? idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: "#1976d2" }}>
                      {item.total_sold}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: "#43a047" }}>
                      {Number(item.total_amount || 0).toLocaleString()} ج.م
                    </TableCell>
                  </TableRow>
                ))}

                {filteredStatistics.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "#888" }}>
                      لا توجد بيانات لعرضها.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ReportStatistics;
